import { createFileRoute, useNavigate, redirect, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import type { CurrentUser } from "../../../lib/auth";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { XIcon } from "../../../components/shared/Icons";
import { backdropClose } from "../../../components/shared/backdrop";

export const Route = createFileRoute("/_authed/agents/new")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin" && u.permissions?.viewer) {
      throw redirect({ to: "/agents" });
    }
  },
  component: NewAgentPage,
});

interface AgentOut {
  id: string;
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-soft)",
  marginBottom: 6,
  display: "block",
};

const ID_REGEX = /^[a-zA-Z0-9_-]+$/;

function NewAgentPage() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [agentId, setAgentId] = useState("");
  const [error, setError] = useState("");
  const [showTplPicker, setShowTplPicker] = useState(false);

  const mutation = useMutation<AgentOut, Error, object>({
    mutationFn: (body) => api.post("/agents", body),
    onSuccess: (data) => {
      navigate({ to: "/agents/$agentId", params: { agentId: data.id } });
    },
    onError: (err) => {
      setError((err as any)?.payload?.detail ?? err.message ?? "Erro ao criar agente");
    },
  });

  // Valida e devolve o corpo base, ou null (com erro setado).
  function validBody(): { name: string; agent_external_id: string; model_provider: string; model_name: string } | null {
    setError("");
    if (!name.trim() || name.trim().length < 2) {
      setError("Nome deve ter ao menos 2 caracteres.");
      return null;
    }
    if (!agentId.trim() || agentId.trim().length < 2) {
      setError("ID do agente deve ter ao menos 2 caracteres.");
      return null;
    }
    if (!ID_REGEX.test(agentId.trim())) {
      setError("ID do agente só pode conter letras, números, hífen e underscore. Sem espaços.");
      return null;
    }
    return { name: name.trim(), agent_external_id: agentId.trim(), model_provider: "openai", model_name: "gpt-4o" };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body = validBody();
    if (!body) return;
    mutation.mutate(body);
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Topbar */}
      <div
        style={{
          height: 52,
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 24px",
          flexShrink: 0,
        }}
      >
        <Link
          to="/agents"
          style={{ color: "var(--ink-muted)", fontSize: 13, textDecoration: "none", lineHeight: 1 }}
        >
          ← Agentes
        </Link>
        <span style={{ color: "var(--line-bold)", fontSize: 13 }}>/</span>
        <h1 style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", margin: 0 }}>Novo agente</h1>
      </div>

      {/* Form */}
      <div style={{ flex: 1, overflowY: "auto", padding: "32px 24px" }}>
        <form
          onSubmit={handleSubmit}
          style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 20 }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label htmlFor="new-agent-name" style={labelStyle}>Nome do projeto</label>
            <input
              id="new-agent-name"
              style={inputStyle}
              placeholder="Ex: Agente de Vendas"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label htmlFor="new-agent-id" style={labelStyle}>ID do agente</label>
            <input
              id="new-agent-id"
              style={inputStyle}
              placeholder="Ex: agente-vendas_01"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value.replace(/\s/g, ""))}
            />
            <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 5 }}>
              Apenas letras, números, hífen e underscore. Não pode ser alterado depois.
            </span>
          </div>

          {error && (
            <div
              style={{
                fontSize: 12,
                color: "var(--danger)",
                background: "var(--danger-soft)",
                border: "1px solid var(--danger-soft-line)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
            <button
              type="submit"
              disabled={mutation.isPending}
              style={{
                padding: "9px 20px",
                background: mutation.isPending ? "var(--brand-line)" : "var(--brand)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontSize: 13,
                fontWeight: 500,
                cursor: mutation.isPending ? "not-allowed" : "pointer",
              }}
            >
              {mutation.isPending ? "Criando..." : "Criar agente"}
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => { if (validBody()) setShowTplPicker(true); }}
              style={{
                padding: "9px 20px",
                background: "var(--surface-input)",
                border: "1px solid var(--line-strong)",
                borderRadius: 8,
                color: "var(--ink)",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Criar a partir de Modelo
            </button>
            <Link
              to="/agents"
              style={{
                padding: "9px 20px",
                background: "var(--surface-input)",
                border: "1px solid var(--line-strong)",
                borderRadius: 8,
                color: "var(--ink-secondary)",
                fontSize: 13,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Cancelar
            </Link>
          </div>
        </form>
      </div>

      {showTplPicker && (
        <TemplatePicker
          pending={mutation.isPending}
          onClose={() => setShowTplPicker(false)}
          onPick={(id) => {
            const body = validBody();
            if (body) mutation.mutate({ ...body, from_template_id: id });
          }}
        />
      )}
    </div>
  );
}

interface AgentTemplate {
  id: string;
  template_kind?: string;  // "clone" | "global" — global não se clona (chips {{ campo.* }} soltos)
  name: string;
  template_description: string | null;
  model_provider: string;
  model_name: string;
}

// Lista os templates de agente; ao escolher, clona no novo agente (menos conexão/CRMillion).
function TemplatePicker({ onPick, onClose, pending }: { onPick: (id: string) => void; onClose: () => void; pending: boolean }) {
  const [search, setSearch] = useState("");
  const { data: templates = [], isLoading } = useQuery<AgentTemplate[]>({
    queryKey: ["dev-agent-templates"],
    queryFn: () => api.get("/dev/agent-templates"),
  });
  const q = search.trim().toLowerCase();
  const clonable = templates.filter((t) => (t.template_kind ?? "clone") !== "global");
  const filtered = q ? clonable.filter((t) => (t.name ?? "").toLowerCase().includes(q)) : clonable;

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label="Criar a partir de modelo" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 520, maxWidth: "95vw", maxHeight: "85vh", padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Criar a partir de modelo</span>
          <button aria-label="Fechar" onClick={onClose} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome..." style={inputStyle} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" }}>
          {isLoading ? (
            <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>Carregando…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>
              {templates.length === 0 ? "Nenhum template de agente cadastrado." : "Nenhum modelo encontrado."}
            </div>
          ) : (
            filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                title={t.template_description || ""}
                disabled={pending}
                onClick={() => onPick(t.id)}
                style={{ textAlign: "left", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.name}</span>
                <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-soft)", border: "1px solid var(--line-strong)", borderRadius: 6, padding: "2px 7px" }}>
                  {t.model_provider}/{t.model_name}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogPanel>
    </div>
  );
}
